"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Button from "../../ui/Button";
import UserIcon from "../../icons/UserIcon";
import SearchIcon from "../../icons/SearchIcon";
import GiftsIcon from "../../icons/GiftsIcon";
import PlusIcon from "../../icons/PlusIcon";
import MinusIcon from "../../icons/MinusIcon";

/**
 * Loyalty illustration at `public/media/pages/dank-club/how-to-phone.webp`:
 * the rewards screen and a club card. This path was referenced for a long time
 * before the file existed, and an onError handler hid the broken image, so the
 * panel rendered as a tidy empty box rather than anything that looked wrong.
 * Keep the file and the path in step.
 */
const HOW_TO_IMAGE = "/media/pages/dank-club/how-to-phone.webp";

type AccordionItem = {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: string[];
  linkLabel?: string;
  link?: string;
};

const accordionItems: AccordionItem[] = [
  {
    id: "join",
    title: "Join the Club",
    icon: <UserIcon size={20} className="w-5 h-5" />,
    content: [
      "Signing up is free & easy. You can create your account online or sign up in-store with one of our budtenders. Start earning points immediately!",
    ],
    linkLabel: "Create Account",
    link: "/register",
  },
  {
    id: "track",
    title: "Track Your Progress",
    icon: <SearchIcon size={20} className="w-5 h-5" />,
    content: [
      "Sign in to your account any time to check your points balance and see how close you are to your next reward.",
    ],
  },
  {
    id: "redeem",
    title: "Redeem Your Rewards",
    icon: <GiftsIcon size={20} className="w-5 h-5" />,
    content: [
      "Ready to cash in? Swap your points for discounts and exclusive perks at checkout, online or in-store.",
    ],
  },
];

export default function DankClubHowToSection() {
  const [expandedId, setExpandedId] = useState<string>("join");

  const toggleAccordion = (id: string) => {
    setExpandedId(expandedId === id ? "" : id);
  };

  return (
    <section className="w-full max-w-none" aria-labelledby="dank-club-how-to-heading">
      <h2
        id="dank-club-how-to-heading"
        className="text-h3-mobile sm:text-h3-desktop font-bold text-text-primary"
      >
        How to use Dank Club
      </h2>

      <div className="mt-6 grid gap-6 lg:grid-cols-2 lg:items-stretch lg:gap-8">
        {/* Accordion, same card pattern as WaysToOrderSection */}
        <div className="flex flex-col gap-4">
          {accordionItems.map((item) => {
            const isExpanded = expandedId === item.id;

            return (
              <div
                key={item.id}
                className="rounded-xl shadow-sm border overflow-hidden transition-all duration-300 ease-in-out bg-constant-white border-blue-5"
              >
                {/* Accordion Header */}
                <div className="flex w-full items-center justify-between gap-3 bg-constant-white p-4 sm:p-5">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div
                      className={`shrink-0 transition-colors duration-200 p-2 rounded-xl bg-blue-5 border border-neutral-10 ${
                        isExpanded ? "text-text-primary" : "text-text-secondary"
                      }`}
                    >
                      {item.icon}
                    </div>
                    <h3
                      id={`dank-club-how-to-heading-${item.id}`}
                      className={`sm:text-lg text-body-16 font-medium transition-colors duration-200 ${
                        isExpanded ? "text-text-primary" : "text-text-secondary"
                      }`}
                    >
                      {item.title}
                    </h3>
                  </div>
                  <Button
                    type="button"
                    variant="icon"
                    color="white"
                    size="icon-md"
                    aria-expanded={isExpanded}
                    aria-controls={`dank-club-how-to-panel-${item.id}`}
                    aria-label={isExpanded ? `Collapse: ${item.title}` : `Expand: ${item.title}`}
                    onClick={() => toggleAccordion(item.id)}
                  >
                    {isExpanded ? (
                      <MinusIcon size={16} color="currentColor" />
                    ) : (
                      <PlusIcon size={16} color="currentColor" />
                    )}
                  </Button>
                </div>

                {/* Accordion Content */}
                <div
                  id={`dank-club-how-to-panel-${item.id}`}
                  role="region"
                  aria-labelledby={`dank-club-how-to-heading-${item.id}`}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isExpanded ? "max-h-[1000px] opacity-100 p-4" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="space-y-4 bg-constant-white">
                    {item.content.map((paragraph, index) => (
                      <p key={index} className="text-body-md text-text-secondary leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                    {item.link && item.linkLabel ? (
                      <div className="pt-2">
                        <Button asChild variant="primary" size="lg" className="rounded-xl py-3 px-6">
                          <Link href={item.link}>{item.linkLabel}</Link>
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Phone illustration */}
        <div className="relative w-full overflow-hidden rounded-2xl border border-neutral-10 bg-bg-2 p-2 sm:p-3">
          <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl bg-constant-white max-lg:aspect-[4/3]">
            <Image
              src={HOW_TO_IMAGE}
              alt="Dank Club rewards shown on a phone, beside a club card"
              fill
              className="object-cover object-center"
              sizes="(max-width: 1023px) 100vw, 50vw"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
